import { ref, readonly, onMounted, onUnmounted } from 'vue'

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance

export function useSpeechRecognition() {
  const isSupported = ref(false)
  const isListening = ref(false)
  const transcript = ref('')
  const error = ref<string | null>(null)

  let recognition: SpeechRecognitionInstance | null = null

  function getSpeechRecognition(): SpeechRecognitionConstructor | null {
    if (typeof window === 'undefined') return null
    return (
      (
        window as unknown as {
          SpeechRecognition?: SpeechRecognitionConstructor
        }
      ).SpeechRecognition ||
      (
        window as unknown as {
          webkitSpeechRecognition?: SpeechRecognitionConstructor
        }
      ).webkitSpeechRecognition ||
      null
    )
  }

  function initialize() {
    const SpeechRecognitionAPI = getSpeechRecognition()

    if (!SpeechRecognitionAPI) {
      isSupported.value = false
      return
    }

    isSupported.value = true
    recognition = new SpeechRecognitionAPI()
    recognition.continuous = false
    recognition.interimResults = false
    recognition.maxAlternatives = 1

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const result = event.results[0]
      if (result && result[0]) {
        transcript.value = result[0].transcript
      }
      isListening.value = false
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const errorMessageKeys: Record<string, string> = {
        'no-speech': "We didn't catch that - tap the mic and try again!",
        'audio-capture':
          "We can't access your microphone. Check your device settings and try again.",
        'not-allowed':
          'Please allow microphone access in your browser to use voice search.',
        network:
          "We're having trouble connecting. Check your internet and try again.",
        aborted: 'Voice search was cancelled. Tap the mic to try again!',
        'language-not-supported':
          "Voice search isn't available in this language yet. Please type your search instead.",
        'service-not-allowed':
          "Voice search isn't available right now. Please type your search instead.",
      }
      error.value =
        errorMessageKeys[event.error] ||
        'Voice search had a hiccup. Please type your search instead.'
      isListening.value = false
    }

    recognition.onend = () => {
      isListening.value = false
    }

    recognition.onstart = () => {
      isListening.value = true
    }
  }

  function start(lang = 'en-GB') {
    if (!recognition) return

    error.value = null
    transcript.value = ''
    recognition.lang = lang

    try {
      recognition.start()
    } catch (e) {
      if (e instanceof Error && e.message.includes('already started')) {
        recognition.stop()
      }
    }
  }

  function stop() {
    if (!recognition) return

    try {
      recognition.stop()
    } catch {
      void 0
    }
    isListening.value = false
  }

  function toggle(lang = 'en-GB') {
    if (isListening.value) {
      stop()
    } else {
      start(lang)
    }
  }

  onMounted(() => {
    initialize()
  })

  onUnmounted(() => {
    if (recognition && isListening.value) {
      try {
        recognition.stop()
      } catch {
        void 0
      }
    }
  })

  return {
    isSupported: readonly(isSupported),
    isListening: readonly(isListening),
    transcript: readonly(transcript),
    error: readonly(error),
    start,
    stop,
    toggle,
  }
}
